import { useEffect, useState, ForwardedRef, forwardRef, useImperativeHandle, useRef, KeyboardEvent } from 'react';
import {phoneNumberAutoFormat, calculatePhoneNumberCursorPosition} from '../../../../utils/format-phone';
import { IContactPhone, IContactPhoneInput } from '../../../../types/contact';
import { useApolloClient } from "@apollo/client";
import { Form, FormFeedback, FormGroup, Input, Label} from 'reactstrap';
import { IValueListLight } from '../../../../types/valuelist';
import { ORG_VALUE_LISTS } from '../../../../graphql/valuelist-gql';
import { numberOnly, validNumber } from '../../../../utils/commons';
import { UserRoleType } from '../../../../types/constants';
import AuthWrapper from '../../../common/auth-wrapper';
import Select from '../../../common/select/select';
import Textarea, { TextareaHandler } from '../../../common/textarea/textarea';
import useAuth from '../../../../hook/useAuth';
import { useFrench } from '../../../french/french-context';

export type PhoneHandler = {
    getContactPhone: () => IContactPhoneInput;
    validate: (inclMandatoryCheck: boolean) => boolean;
}

type PhoneDetailsProps = {
    contactPhone?: IContactPhone;
    isOrgPhone: boolean;
    onChange: () => void;
    autoFocus?: boolean;
}

const PhoneDetails = forwardRef<PhoneHandler, PhoneDetailsProps>((props: PhoneDetailsProps, ref:ForwardedRef<any>) => {
    const {contactPhone, isOrgPhone, onChange, autoFocus} = props

    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [contactPhoneInput, setContactPhoneInput] = useState<Partial<IContactPhoneInput>>()

    const [phoneTypeError, setPhoneTypeError] = useState<string>('')
    const [phoneNumberError, setPhoneNumberError] = useState<string>('')
    const [phoneExtError, setPhoneExtError] = useState<string>('')

    const [inlineVaidate, setInlineValidate] = useState<boolean>(false)

    const descRef = useRef<TextareaHandler>(null)
    const phoneInputRef = useRef<HTMLInputElement>(null)

    const { isFrenchTranslator } = useAuth()
    const [frenchState] = useFrench()

    useEffect(() => {
        setContactPhoneInput({
            contactPhoneId: contactPhone?.id || 0,
            phoneTypeId: contactPhone?.phoneType?.id || getDefaultPhoneType(),
            countryCode: contactPhone?.countryCode || '',
            extension: contactPhone?.extension || '',
            description: contactPhone?.description || '',
            descriptionFrench: contactPhone?.descriptionFrench || '',
            descriptionFrenchSync: contactPhone?.descriptionFrenchSync || 0
        })
        if (contactPhone) {
            setPhoneNumber(phoneNumberAutoFormat(contactPhone.areaCode + contactPhone.localNumber))
        }
    }, [contactPhone])

    const client = useApolloClient()
    const { orgValueLists } = client.readQuery({query: ORG_VALUE_LISTS})

    useImperativeHandle(ref, () => ({
        getContactPhone(){
            let _phoneNumber = phoneNumber.replaceAll("-", "")
            return {
                ...contactPhoneInput,
                areaCode: _phoneNumber.length > 3 ? _phoneNumber.substring(0, 3) : '',
                localNumber: _phoneNumber.length > 3 ? _phoneNumber.substring(3) : '',
            }
        },
        validate(inclMandatoryCheck: boolean): boolean {
            let valid: boolean =  validateExt(contactPhoneInput?.extension) && (!descRef.current || !!descRef.current?.validate())

            if (valid && inclMandatoryCheck) {
                setInlineValidate(true)
                if (!contactPhoneInput?.phoneTypeId) {
                    setPhoneTypeError("Phone type is required.")
                    valid = false
                }
                if (!phoneNumber) {
                    setPhoneNumberError("Phone number is required.")
                    valid = false
                }
                if (!validPhoneNum(phoneNumber)) {
                    setPhoneNumberError("This value should be a ten-digit number.")
                    valid = false
                }
            }

            return valid
        }
    }),[contactPhoneInput, phoneNumber])

    useEffect(() => {
        onChange()
    }, [contactPhoneInput, phoneNumber])

    const validPhoneNum = (phoneNumber: string) => {
        let _phoneNumber = phoneNumber.replaceAll("-", "")
        return _phoneNumber.length >= 10
    }

    const getPhoneTypes = () => {
        return isOrgPhone ? orgValueLists?.phoneTypes : orgValueLists?.posPhoneTypes;
    }

    const getDefaultPhoneType = () => {
        return isOrgPhone ? orgValueLists?.phoneTypes?.find((e: IValueListLight) => e.code === 'GENIQ')?.id
            : orgValueLists?.posPhoneTypes?.find((e: IValueListLight) => e.code === 'WORK')?.id
    }

    const changePhoneTypeId = (event:any)=> {
        console.log(event.target.value);
        setContactPhoneInput({...contactPhoneInput, phoneTypeId: event.target.value})
    }

    const changeCountryCode = (event:any)=> {
        const cc = numberOnly(event.target.value)
        setContactPhoneInput({...contactPhoneInput, countryCode: cc})
    }

    const changePhoneNumber = (event:any)=> {
        const input: string = event.target.value;
        const selectionStart = event.target.selectionStart;
        const formatted: string = phoneNumberAutoFormat(input);

        setPhoneNumber(formatted);

        // set the cursor position manually to account for dashes "-"
        setTimeout(() => {
        if (phoneInputRef.current) {
            const cursorPos = calculatePhoneNumberCursorPosition(input, formatted, selectionStart || 0);
            phoneInputRef.current.setSelectionRange(cursorPos, cursorPos);
            }
        }, 0);
    }

    const changeExtension = (event:any)=> {
        validateExt(event.target.value.trim())
        setContactPhoneInput({...contactPhoneInput, extension: event.target.value.trim()})
    }

    const validateExt = (ext: string | undefined): boolean => {
        if (ext && !validNumber(ext, 5)) {
            setPhoneExtError("Invalid extension number. Please enter 1-5 digits (no letters or symbols allowed).")
            return false
        }
        if (inlineVaidate && !validPhoneNum(phoneNumber)) {
            setPhoneNumberError("This value should be a ten-digit number.")
            return false
        }
        setPhoneExtError("")
        return true
    }

    const changeDescription = (value: string) => {
        if (value && value.trim().length > 0) {
            setContactPhoneInput({...contactPhoneInput, description: value, descriptionFrenchSync:0})
        }
        else {
            setContactPhoneInput({...contactPhoneInput, description: '', descriptionFrench: '', descriptionFrenchSync: 0})
        }
    }

    const changeDescriptionTranslation = (event: any) => {
        setContactPhoneInput({...contactPhoneInput, descriptionFrenchSync: event.target.checked? 1:0})
    }

    const phoneInputKeydown = (event: KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'Backspace' && phoneInputRef.current) {
            const start = phoneInputRef.current.selectionStart || 0;
            const end = phoneInputRef.current.selectionEnd || 0;
    
            if (start !== end) {
                // Prevent the default backspace behavior
                event.preventDefault();
    
                // Remove the highlighted portion of the string
                const newValue = phoneNumber.slice(0, start) + phoneNumber.slice(end);
    
                // Format the new value
                const formattedValue = phoneNumberAutoFormat(newValue);
                setPhoneNumber(formattedValue);
    
                // Adjust the cursor position
                setTimeout(() => {
                    const cursorPos = start; // The cursor should stay at the start of the selection
                    phoneInputRef.current?.setSelectionRange(cursorPos, cursorPos);
                }, 0);
            } else {
                // Handle case when deleting a single character (no highlight)
                const cursorPosition = phoneInputRef.current.selectionStart;
                if (cursorPosition && phoneNumber[cursorPosition - 1] === '-') {
                    event.preventDefault();
    
                    const newValue =
                        phoneNumber.slice(0, cursorPosition - 2) +
                        phoneNumber.slice(cursorPosition);
    
                    setPhoneNumber(phoneNumberAutoFormat(newValue));
    
                    setTimeout(() => {
                        const newCursorPos = cursorPosition - 2;
                        phoneInputRef.current?.setSelectionRange(newCursorPos, newCursorPos);
                    }, 0);
                }
            }
        }
    } 
    
    return (
        <Form className='oms-container'>
            
                <div className='row'>
                    <div className='col-md-4'><label htmlFor="phoneType">Phone Type: (required)</label></div>
                    {!isFrenchTranslator && <div className='col-md-8'>
                        <Select id="phoneType"
                            value={contactPhoneInput?.phoneTypeId}
                            onChange={changePhoneTypeId}
                            style={{width:'100%'}}
                            invalid={!!phoneTypeError}
                            required
                            autoFocus={autoFocus}>
                            {getPhoneTypes()?.map((t: IValueListLight) => <option key={t.id} value={t.id}>{t.label}</option>)}
                        </Select>
                        <FormFeedback>{phoneTypeError}</FormFeedback>
                    </div>
                    }
                    {
                        isFrenchTranslator && <div className='col-md-8'>
                           {getPhoneTypes().map((item: IValueListLight) => {
                                    if (item.id === contactPhoneInput?.phoneTypeId) {
                                        return item.label;
                                    }
                                    return '';
                                })}
                        </div>
                    }
                </div>

                <div className='row'>
                    <div className='col-md-4'><label htmlFor="countryCode">Country Code:</label></div>
                    {!isFrenchTranslator && <div className='col-md-8'>
                        <AuthWrapper role={[UserRoleType.ADMINISTRATOR]}>
                            {(authorized:boolean)=>
                                <Input id="countryCode" type="text" style={{ width: '100%' }} maxLength={3} value={contactPhoneInput?.countryCode}  onChange={changeCountryCode} placeholder='Enter a country code' disabled={!authorized}/>
                            }
                        </AuthWrapper>
                    </div>
                    }
                    {
                        isFrenchTranslator && <div className='col-md-8' ><span id="countryCode">{contactPhoneInput?.countryCode}</span></div>
                    }
                </div>
                <div className='row'>
                    <div className='col-md-4'><label htmlFor="phoneNumber">Phone Number: (required)</label></div>
                    {!isFrenchTranslator &&  <div className='col-md-8'>
                        <Input id="phoneNumber"
                            type="tel"
                            style={{ width: '100%' }}
                            value={phoneNumber}
                            onChange={changePhoneNumber}
                            placeholder='Enter a ten-digit phone number'
                            maxLength={12}
                            innerRef={phoneInputRef}
                            invalid={!!phoneNumberError}
                            onKeyDown={phoneInputKeydown}
                            required/>
                        <FormFeedback>{phoneNumberError}</FormFeedback>
                    </div>
                    }
                    {
                        isFrenchTranslator && <div className='col-md-8'><span id="phoneNumber">{phoneNumber}</span></div>
                    }
                </div>
                <div className='row'>
                    <div className='col-md-4'><label htmlFor="extension">Extension: </label></div>
                    {!isFrenchTranslator &&<div className='col-md-8'>
                        <Input id="extension"
                            type="text"
                            style={{ width: '100%' }}
                            value={contactPhoneInput?.extension}
                            onChange={changeExtension}
                            placeholder='Enter an extension'
                            invalid={!!phoneExtError}/>
                        <FormFeedback>{phoneExtError}</FormFeedback>
                    </div>
                    }
                    {
                        isFrenchTranslator && <div className='col-md-8'  ><span id="extension">{contactPhoneInput?.extension}</span></div>
                    }
                </div>
                {isOrgPhone &&
                    <div className='row'>
                        <div className='col-md-4'><label htmlFor="phoneDesc">Description: </label></div>
                        {!isFrenchTranslator &&<div className='col-md-8'>
                            <Textarea id="phoneDesc"
                                ref={descRef}
                                rows={3}
                                maxLength={255}
                                value={contactPhoneInput?.description}
                                onValueChange={changeDescription}
                                placeholder='Enter a description for the phone number'
                            />
                        </div>
                        }
                        {
                        isFrenchTranslator && <div className='col-md-8'><span id="phoneDesc" >{contactPhoneInput?.description}</span></div>
                    }
                    </div>
                }
                {frenchState.isFrenchChecked && isOrgPhone &&
                    <>
                        <div className='row'>
                            <div className='col-md-4'><label htmlFor="phoneDescFrench">Description (French): </label></div>
                            <div className='col-md-8'><span id='phoneDescFrench' lang="fr">{contactPhoneInput?.descriptionFrench}</span></div>
                        </div>
                        <div className='row'>
                            <div className='col-md-12'>
                                <Input id="descriptionFrenchSync" type="checkbox" checked={contactPhoneInput?.descriptionFrenchSync === 1 ? true : false} onChange={changeDescriptionTranslation} disabled = {!contactPhoneInput?.descriptionFrench} /> {' '}
                                <label htmlFor="descriptionFrenchSync">Translated Description</label>
                            </div>
                        </div>
                    </>
                }
        </Form>

    )
})
export default PhoneDetails
